// pages/SAPDocInspector.jsx — "Document check" tab of the Process Map.
// Live: GET /api/sap-procmap/doc?land=&type=lot|po|so|order|mplan&no=
// Shows key facts, active system statuses and plain-language hints on why a typical
// action (cancel, change, deliver, post) is blocked — for answering user tickets fast.
import { useState, useEffect, useRef } from 'react';
import { api } from '../context/AuthContext';

const TYPES = [
  ['matdoc', { en: 'Material document', id: 'Material document' }, 'MIGO / MB03'],
  ['lot', { en: 'Inspection lot', id: 'Inspection lot' }, 'QA03'],
  ['po', { en: 'Purchase order', id: 'Purchase order' }, 'ME23N'],
  ['so', { en: 'Sales document', id: 'Dokumen sales' }, 'VA03'],
  ['order', { en: 'PM / PP order', id: 'Order PM / PP' }, 'IW33 / CO03'],
  ['mplan', { en: 'Maintenance plan', id: 'Maintenance plan' }, 'IP03'],
  ['bom', { en: 'BOM (material)', id: 'BOM (material)' }, 'CS03'],
  ['rtg', { en: 'Routing (material)', id: 'Routing (material)' }, 'CA03'],
  ['equi', { en: 'Equipment / Func. loc.', id: 'Equipment / Func. loc.' }, 'IE03 / IL03'],
  ['mat', { en: 'Material', id: 'Material' }, 'MM03'],
  ['shpcost', { en: 'Shipment cost (vendor)', id: 'Shipment cost (vendor)' }, 'TK13'],
  ['mlist', { en: 'Material list / search', id: 'Daftar / cari material' }, 'MM03 / MM60'],
];
// Master-data types: alphanumeric key; bom/rtg/mat/mlist also take an optional plant.
const MD = ['bom', 'rtg', 'equi', 'mat', 'shpcost', 'mlist'];
const PLANT = ['bom', 'rtg', 'mat', 'mlist'];
const PH = { bom: 'Material', rtg: 'Material', equi: 'Equipment / FL', mat: 'Material', shpcost: 'Vendor no. or name',
  mlist: 'Numbers (1712300131-1712300136, commas or one per line) or a description to search' };
// Allowed characters per input kind (shipment cost accepts vendor names such as "PT. TAS"; the material list
// accepts pasted lists from a ticket, so newlines, commas and ranges are kept).
const clean = (type, s) => (type === 'mlist' ? s.replace(/[^A-Za-z0-9 ,;.\-/&()+#\n]/g, '').toUpperCase()
  : type === 'shpcost' ? s.replace(/[^A-Za-z0-9 .&-]/g, '').toUpperCase()
  : MD.includes(type) ? s.replace(/[^A-Za-z0-9\-_./]/g, '').toUpperCase() : s.replace(/\D/g, ''));
const COLS = {
  matdoc: [['item', 'Item'], ['mvt', 'MvT'], ['mat', 'Material'], ['qty', 'Qty'], ['plant', 'Plant'], ['sloc', 'SLoc'], ['ref', 'Reference'], ['flags', 'Status']],
  po: [['item', 'Item'], ['mat', 'Material'], ['text', 'Text'], ['qty', 'Qty'], ['plant', 'Plant'], ['sloc', 'SLoc'], ['gr', 'GR qty'], ['ir', 'IR qty'], ['flags', 'Flags']],
  so: [['item', 'Item'], ['mat', 'Material'], ['text', 'Text'], ['qty', 'Qty'], ['plant', 'Plant'], ['sloc', 'SLoc'], ['flags', 'Flags']],
  mplan: [['item', 'Call'], ['text', 'Planned date'], ['plant', 'Call date'], ['qty', 'Status'], ['flags', 'Sched. ind.']],
  bom: [['plant', 'Plant / Alt'], ['item', 'Item'], ['mvt', 'Cat.'], ['mat', 'Component'], ['text', 'Description'], ['qty', 'Qty'], ['ref', 'Valid from'], ['flags', 'Flags']],
  rtg: [['plant', 'Routing'], ['item', 'Op.'], ['mvt', 'Ctrl key'], ['ref', 'Work center'], ['text', 'Text'], ['qty', 'Base qty'], ['flags', 'Std values / flags']],
  equi: [['item', 'Plan / Order / Equipment'], ['mat', 'Plan item'], ['text', 'Text'], ['plant', 'Order type / plant'], ['qty', 'Note'], ['flags', 'Status']],
  shpcost: [['cond', 'Cond.'], ['route', 'Route / Vendor'], ['key', 'Key'], ['rate', 'Rate'], ['valid', 'Valid'], ['text', 'Created'], ['flags', 'Status']],
  mlist: [['mat', 'Material'], ['text', 'Description'], ['mvt', 'Type · group'], ['plant', 'Plants'], ['ref', 'Views'], ['qty', 'BOM / routing / PV'], ['flags', 'Created · flags']],
  mat: [['plant', 'Plant'], ['text', 'Procurement · MRP'], ['mvt', 'Price ctrl · class'], ['qty', 'Price / unit'], ['ref', 'Storage locations'], ['flags', 'Flags']],
};
const T = {
  en: { title: 'Check a document', intro: 'Enter a document number to see its status and why an action may be blocked. Read-only, live from SAP.', no: 'Number', go: 'Check', busy: 'Reading SAP…',
    facts: 'Key facts', status: 'Active system status', items: 'Items', hints: 'What this means', none: 'No specific blocker detected.', tc: 'Display in SAP' },
  id: { title: 'Cek dokumen', intro: 'Masukkan nomor dokumen untuk melihat status dan alasan sebuah aksi terblokir. Hanya baca, live dari SAP.', no: 'Nomor', go: 'Cek', busy: 'Membaca SAP…',
    facts: 'Fakta utama', status: 'System status aktif', items: 'Item', hints: 'Artinya', none: 'Tidak ada penghalang khusus yang terdeteksi.', tc: 'Lihat di SAP' },
};
const HINT = { block: 'bg-rose-50 border-rose-200 text-rose-800', warn: 'bg-amber-50 border-amber-200 text-amber-900', ok: 'bg-emerald-50 border-emerald-200 text-emerald-800' };
const ICON = { block: '⛔', warn: '⚠️', ok: '✅' };

export default function SAPDocInspector({ landKey, lang }) {
  const L = lang === 'id' ? 'id' : 'en';
  const t = T[L];
  // Deep link ?doctype=&docno= (dari AI Propose Solution di modal tiket SolMan): isi form lalu langsung cek.
  const qp = (() => { try { return new URLSearchParams(window.location.search); } catch (_) { return new URLSearchParams(); } })();
  const [type, setType] = useState(() => (TYPES.some(x => x[0] === qp.get('doctype')) ? qp.get('doctype') : 'matdoc'));
  const [no, setNo] = useState(() => qp.get('docno') || '');
  const autoRan = useRef(false);
  const [werks, setWerks] = useState('');
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  async function run(e) {
    if (e) e.preventDefault();
    if (!no.trim()) return;
    setBusy(true); setErr(''); setData(null);
    try {
      const { data: d } = await api.get('/api/sap-procmap/doc', { params: { land: landKey, type, no: no.trim(), ...(PLANT.includes(type) && werks.trim() ? { werks: werks.trim() } : {}) }, timeout: 180000 });
      setData(d);
    } catch (e2) { setErr((e2.response && e2.response.data && e2.response.data.error) || e2.message); }
    finally { setBusy(false); }
  }
  useEffect(() => {
    if (autoRan.current || !qp.get('docno')) return;
    autoRan.current = true;
    run();
  }, []); // sekali saat mount
  const tc = (TYPES.find(x => x[0] === type) || [])[2];
  const cols = data && COLS[data.type];

  return (
    <div className="bg-white border border-slate-200 rounded-xl px-4 py-3 flex flex-col gap-3">
      <div>
        <div className="text-[15px] font-bold text-slate-800">{t.title}</div>
        <p className="text-[12.5px] text-slate-500 m-0">{t.intro}</p>
      </div>
      <form onSubmit={run} className="flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1">
          {TYPES.map(([k, lbl]) => (
            <button key={k} type="button" onClick={() => { if (MD.includes(k) !== MD.includes(type) || ['shpcost', 'mlist'].includes(k) || ['shpcost', 'mlist'].includes(type)) setNo(''); setType(k); setData(null); setErr(''); }}
              className={`px-2.5 py-1 rounded-md text-[12px] font-semibold border ${type === k ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'}`}>{lbl[L]}</button>
          ))}
        </div>
        {type === 'mlist' ? (
          // Multi-line: paste the numbers straight from the ticket. Ctrl/Cmd+Enter submits.
          <textarea value={no} onChange={e => setNo(clean(type, e.target.value))} rows={3} placeholder={PH.mlist}
            onKeyDown={e => { if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) run(e); }}
            className="border border-slate-200 rounded-md px-2 py-1 text-[13px] font-mono w-full sm:w-[420px] max-w-full" />
        ) : (
          <input value={no} onChange={e => setNo(clean(type, e.target.value))}
            placeholder={PH[type] || t.no} inputMode={MD.includes(type) ? 'text' : 'numeric'}
            className="border border-slate-200 rounded-md px-2 py-1 text-[13px] font-mono w-[180px]" />
        )}
        {PLANT.includes(type) && (
          <input value={werks} onChange={e => setWerks(e.target.value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 4))} placeholder={L === 'id' ? 'Plant (opsional)' : 'Plant (optional)'}
            className="border border-slate-200 rounded-md px-2 py-1 text-[13px] font-mono w-[130px]" />
        )}
        <button type="submit" disabled={busy || !no} className="px-3 py-1 rounded-md text-[12.5px] font-semibold border bg-brand-600 text-white border-brand-600 disabled:opacity-60">
          {busy ? t.busy : t.go}
        </button>
        {tc && <span className="text-[11.5px] text-slate-400">{t.tc}: <span className="font-mono">{tc}</span></span>}
      </form>
      {err && <div className="text-[12.5px] text-rose-600">{err}</div>}

      {data && (
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.hints}</div>
            {data.hints.length ? data.hints.map((h, i) => (
              <div key={i} className={`border rounded-lg px-3 py-2 text-[12.5px] ${HINT[h.lvl]}`}>{ICON[h.lvl]} {h[L]}</div>
            )) : <div className="text-[12.5px] text-slate-500">{t.none}</div>}
          </div>

          <div className="grid gap-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))' }}>
            <div className="flex flex-col gap-1.5 min-w-0">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.facts}</div>
              <table className="text-[12.5px] w-full"><tbody>
                {data.facts.map(([k, v]) => (
                  <tr key={k} className="border-t border-slate-100 align-top">
                    <td className="py-1 pr-3 text-slate-500 whitespace-nowrap">{k}</td>
                    <td className="py-1 text-slate-800 break-words">{String(v)}</td>
                  </tr>
                ))}
              </tbody></table>
            </div>
            {data.status && data.status.length > 0 && (
              <div className="flex flex-col gap-1.5 min-w-0">
                <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.status}</div>
                <div className="flex flex-wrap gap-1">
                  {data.status.map(s => (
                    <span key={s.stat} title={s.stat} className="rounded border border-slate-200 bg-slate-50 px-1.5 py-[1px] text-[11.5px]">
                      <b className="font-mono text-slate-700">{s.code}</b> <span className="text-slate-500">{s.text}</span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {cols && data.items.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-400">{t.items} · {data.items.length}</div>
              <div className="overflow-x-auto border border-slate-200 rounded-lg max-h-[360px] overflow-y-auto">
                <table className="w-full text-[12px] border-collapse">
                  <thead className="sticky top-0"><tr className="bg-slate-50 text-slate-500 text-[11px]">
                    {cols.map(([k, h]) => <th key={k} className="px-2 py-1 text-left font-semibold whitespace-nowrap">{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {data.items.map((r, i) => (
                      <tr key={i} className="border-t border-slate-100">
                        {cols.map(([k]) => <td key={k} className="px-2 py-1 whitespace-nowrap">{r[k] === '' || r[k] === undefined ? '—' : typeof r[k] === 'number' ? r[k].toLocaleString() : r[k]}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="text-[11px] text-slate-400">{new Date(data.at).toLocaleString()}</div>
        </div>
      )}
    </div>
  );
}
